import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  NgZone,
  Output,
  ViewChild,
} from '@angular/core';
import {JsonPipe, NgTemplateOutlet} from '@angular/common';
import {BicBadgeComponent, TooltipDirective} from '@buildinginformationcloud/bic-ui-components';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {IconDefinition} from '@fortawesome/fontawesome-svg-core';
import {
  faChevronDown,
  faChevronUp,
  faCircleCheck,
  faCircleExclamation,
  faCircleInfo,
  faCircleQuestion,
  faCircleXmark,
  faFileCircleXmark,
} from '@fortawesome/pro-solid-svg-icons';
import {CardGroup, CardModel, CardSection, EMPTY_CARD_MODEL,} from '../../../../../models/report/report-card-model';
import {ReportChip} from '../../../../../models/report/normalized-report.model';
import {EmptyStateComponent} from '../../../../util/design/empty-state/empty-state.component';
import {MarkdownModule} from 'ngx-markdown';
import {NormalizeMarkdownPipe} from '../../../../../pipes/normalize-markdown.pipe';
import {ReportSourceLinksPipe} from '../../../../../pipes/report-source-links.pipe';
import {ReportSourceLinkDirective} from '../../../../../directives/report-source-link.directive';
import {StateConfigService} from '../../../../../services/state/state-config.service';
import {ReportRowActionsComponent} from '../report-row-actions/report-row-actions.component';

type Status = 'success' | 'warning' | 'error' | 'unknown';

export interface ReportCardTaskContext {
  ruleName: string;
  facetName: string;
}

/**
 * Kartenansicht des Prüfberichts: Datei-Header → (optional) Positions-Header → eine Karte je
 * Prüfregel mit aufklappbaren Ergebnis-Blöcken.
 *
 * Die Komponente ist bewusst rein darstellend. Das gesamte View-Modell kommt fertig aus
 * `buildCardModel` (siehe `models/report/report-card-model.ts`), das aus derselben
 * Berichtskonfiguration abgeleitet wird wie die Spaltenansicht – hier steht deshalb keine
 * Ableitungs-, Filter- oder Prüfungsart-Logik.
 *
 * Auch der Aufklappzustand liegt außerhalb: die Kopfzeile der Seite trägt den
 * „Aufklappen/Einklappen"-Knopf und braucht denselben Zustand, deshalb hält ihn die
 * Elternkomponente und gibt ihn hier herein.
 */
@Component({
  selector: 'app-report-card-view',
  standalone: true,
  imports: [
    BicBadgeComponent,
    EmptyStateComponent,
    FaIconComponent,
    JsonPipe,
    NgTemplateOutlet,
    TooltipDirective,
    MarkdownModule,
    NormalizeMarkdownPipe,
    ReportRowActionsComponent,
    ReportSourceLinksPipe,
    ReportSourceLinkDirective,
  ],
  templateUrl: './report-card-view.component.html',
  styleUrl: './report-card-view.component.scss',
  host: {
    class: 'w-full flex-1 flex flex-col min-h-0',
  },
  /**
   * OnPush ist hier gefahrlos möglich, weil die Komponente rein darstellend ist und ihre beiden
   * Eingaben immer ERSETZT werden, nie verändert: `model` kommt frisch aus `buildCardModel`, und
   * `expandedKeys` wird in der Elternkomponente jedes Mal als neues Set gesetzt (`toggleCard`,
   * `toggleAllCards`). Ohne OnPush prüft Angular in jedem Zyklus der Seite den kompletten
   * Kartenbaum durch – bei einem KI-Bericht der Großteil der Arbeit pro Zyklus.
   */
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportCardViewComponent implements AfterViewInit {
  /**
   * Karten, die beim ersten Rendern aufgebaut werden, und Schrittweite beim Nachladen.
   *
   * 60 eingeklappte Karten füllen jeden Bildschirm sicher aus, sodass das Nachladen immer über das
   * Scroll-Ereignis ausgelöst wird. Der Wert liegt außerdem über allem, was die Tests der
   * Komponente rendern – dort ist das Fenster damit ohne Wirkung.
   *
   * Das gilt nur, weil die Zwischenüberschriften MIT im Fenster liegen (siehe
   * {@link isGroupRendered}). Solange sie immer alle gerendert wurden, endete die Liste mitten
   * drin: bei der Ebenenfolge Datei → Prüfregel → Strukturebene sind die Karten die Positionen,
   * und hinter den ersten ~60 standen sämtliche weiteren Prüfregel-Überschriften ohne eine
   * einzige Position darunter. Nachgeladen wurde dann auch nichts mehr, weil die leeren
   * Überschriften den Container hoch machten und die neuen Karten weit oberhalb des
   * Sichtbereichs einfügten.
   */
  private static readonly RENDER_CHUNK = 60;
  private readonly zone = inject(NgZone);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly stateConfigService = inject(StateConfigService);
  @ViewChild('scrollContainer') private scrollContainer?: ElementRef<HTMLElement>;
  private renderLimit = ReportCardViewComponent.RENDER_CHUNK;
  private renderedKeys = new Set<string>();
  /** Keys der Abschnitte/Gruppen, von denen mindestens eine Karte im Fenster liegt. */
  private renderedSectionKeys = new Set<string>();
  private renderedGroupKeys = new Set<string>();

  private _model: CardModel = EMPTY_CARD_MODEL;

  get model(): CardModel {
    return this._model;
  }

  @Input()
  set model(value: CardModel) {
    this._model = value ?? EMPTY_CARD_MODEL;
    // Neuer Bericht oder neue Filterauswahl: wieder von vorn, sonst würde ein gescrolltes Fenster
    // aus dem vorherigen Modell die erste Bildschirmseite des neuen mitbestimmen.
    this.renderLimit = ReportCardViewComponent.RENDER_CHUNK;
    this.updateRenderedKeys();
  }

  /** Keys der aufgeklappten Karten; gehört der Elternkomponente (siehe Klassen-Doku). */
  @Input() expandedKeys = new Set<string>();

  /** Meldet den Wunsch, eine Karte auf-/zuzuklappen; die Elternkomponente pflegt das Set. */
  @Output() toggleItem = new EventEmitter<string>();
  /** Meldet die Facette, für die aus der Kartenansicht eine Aufgabe erstellt werden soll. */
  @Output() taskClicked = new EventEmitter<ReportCardTaskContext>();

  /** Projekt des Berichts – Linkziel der Quellen-Badges im KI-Antworttext. */
  protected get sourceLinkProjectId(): number | null {
    return this.stateConfigService.getProjectId() || null;
  }

  protected readonly faChevronDown = faChevronDown;
  protected readonly faChevronUp = faChevronUp;
  protected readonly faCircleInfo = faCircleInfo;
  protected readonly faFileCircleXmark = faFileCircleXmark;

  isExpanded(key: string): boolean {
    return this.expandedKeys.has(key);
  }

  /** Ob die Karte im aktuellen Render-Fenster liegt (siehe {@link RENDER_CHUNK}). */
  isRendered(key: string): boolean {
    return this.renderedKeys.has(key);
  }

  /**
   * Ob der Datei-/Gruppen-Header gezeigt wird: nur, wenn mindestens eine Karte darunter im
   * Fenster liegt. Eine Überschrift ohne jede Karte ist keine Information, sondern eine Lücke.
   */
  isGroupRendered(group: CardGroup): boolean {
    return !group.node || this.renderedGroupKeys.has(group.node.key);
  }

  /** Wie {@link isGroupRendered}, für den Positions-/Prüfregel-Zwischenheader. */
  isSectionRendered(section: CardSection): boolean {
    return !section.node || this.renderedSectionKeys.has(section.node.key);
  }

  /**
   * Scroll-Überwachung für das Nachladen – bewusst außerhalb der Angular-Zone.
   *
   * Ein `(scroll)`-Binding im Template würde bei jedem einzelnen Scroll-Ereignis einen
   * Change-Detection-Zyklus auslösen und damit genau die Last zurückholen, die OnPush hier
   * einspart. Ein Zyklus wird deshalb nur angestoßen, wenn das Fenster wirklich wächst.
   */
  ngAfterViewInit(): void {
    const container = this.scrollContainer?.nativeElement;
    if (!container) return;
    this.zone.runOutsideAngular(() => {
      container.addEventListener('scroll', () => this.extendIfNearEnd(container), {passive: true});
    });
  }

  /**
   * Erweitert das Render-Fenster, sobald das Ende der Liste in Reichweite kommt (Vorlauf: eine
   * Bildschirmhöhe). Die einmal gebauten Karten bleiben stehen – kein Recycling; außerhalb des
   * Sichtbereichs nimmt `content-visibility` sie dem Layout ab (siehe SCSS).
   */
  private extendIfNearEnd(container: HTMLElement): void {
    if (this.renderedKeys.size >= this._model.itemKeys.length) return;
    const remaining = container.scrollHeight - container.scrollTop - container.clientHeight;
    if (remaining > container.clientHeight) return;
    this.zone.run(() => {
      this.renderLimit += ReportCardViewComponent.RENDER_CHUNK;
      this.updateRenderedKeys();
      this.cdr.markForCheck();
    });
  }

  private updateRenderedKeys(): void {
    const rendered = new Set(this._model.itemKeys.slice(0, this.renderLimit));
    const sections = new Set<string>();
    const groups = new Set<string>();

    for (const group of this._model.groups) {
      let groupVisible = false;
      for (const section of group.sections) {
        if (!section.items.some(item => rendered.has(item.node.key))) continue;
        groupVisible = true;
        if (section.node) sections.add(section.node.key);
      }
      if (groupVisible && group.node) groups.add(group.node.key);
    }

    this.renderedKeys = rendered;
    this.renderedSectionKeys = sections;
    this.renderedGroupKeys = groups;
  }

  /** Farbe des 6px-Statusstreifens links an der Karte. */
  stripeClass(status: Status): string {
    switch (status) {
      case 'success':
        return 'bg-green';
      case 'warning':
        return 'bg-yellow';
      case 'error':
        return 'bg-red';
      case 'unknown':
        return 'bg-gray-400';
    }
  }

  statusIcon(status: Status): IconDefinition {
    switch (status) {
      case 'success':
        return faCircleCheck;
      case 'warning':
        return faCircleExclamation;
      case 'error':
        return faCircleXmark;
      case 'unknown':
        return faCircleQuestion;
    }
  }

  statusTextClass(status: Status): string {
    switch (status) {
      case 'success':
        return 'text-green';
      case 'warning':
        return 'text-yellow';
      case 'error':
        return 'text-red';
      case 'unknown':
        return 'text-gray-400';
    }
  }

  /** Badge-Hintergrund für 'unknown' (die Library kennt nur info/success/warning/error als `type`). */
  badgeBgClass(type: Status | 'info'): string | undefined {
    return type === 'unknown' ? 'bg-gray-100' : undefined;
  }

  /** Badge-Textfarbe für 'unknown' (siehe {@link badgeBgClass}). */
  badgeTextClass(type: Status | 'info'): string | undefined {
    return type === 'unknown' ? 'text-gray-500' : undefined;
  }

  /** Vollständiger Chip-Text („Entität: IfcSlab") inklusive der zusammengefassten Restwerte. */
  chipTooltip(chips: ReadonlyArray<ReportChip>): string {
    return chips
      .map(chip => {
        const values = [chip.value, ...(chip.rest ?? [])].filter(Boolean).join(', ');
        return chip.label ? `${chip.label}: ${values}` : values;
      })
      .join(' · ');
  }

  requestTaskCreation(ruleName: string, facetName: string): void {
    this.taskClicked.emit({ruleName, facetName});
  }

  copyFacetResponse(ruleName: string, facetName: string): void {
    const text = `Regel: ${ruleName}\nResponse: ${facetName}`;
    if (!navigator?.clipboard?.writeText) return;
    navigator.clipboard.writeText(text);
  }
}
